import { SortableContext } from "@dnd-kit/sortable";
import { type FC, useState } from "react";
import { ActionButton, ActionButtonIntent, ActionButtonSize, Card, ColorButton, Dialog } from "widgets";

import style from "@/modules/files/styles/FileBrowserActions.module.scss";

import { getCssColor } from "../../../../app-utils/color.js";
import { type Phenotype, type TrackPhenotypeTermConfiguration } from "../../../../graphql.js";
import { type IConfiguredPhenotype } from "../../../../redux/modules/genome-browser.js";
import { ContentPlaceholder } from "../../../core/ContentPlaceholder.js";
import { SortableItem, SortableList } from "../../../core/forms/Sortable.js";
import sortableStyle from "../../../core/forms/styles/Sortable.module.scss";
import { type IPopoverPropsInjectedFromController } from "../../../core/overlays/components/OverlayControllers.js";

interface IPhenotypeTermConfigDialogFormProps extends IPopoverPropsInjectedFromController {
  phenotype: IConfiguredPhenotype;
  onApply: (configuredPhenotype: IConfiguredPhenotype) => void;
}

const PhenotypeTermConfigDialogForm: FC<IPhenotypeTermConfigDialogFormProps> = (props) => {
  const [configuredTerms, setConfiguredTerms] = useState<TrackPhenotypeTermConfiguration[]>(
    props.phenotype.configuredTerms || [],
  );

  const onSubmit = () => {
    const configuredPhenotype: IConfiguredPhenotype = {
      ...props.phenotype,
      configuredTerms: configuredTerms,
    };
    props.onApply(configuredPhenotype);
  };

  const handleColorModification = (color: string, term: Phenotype) => {
    const configuredTerm: TrackPhenotypeTermConfiguration = {
      term: term.value,
      color: color,
    };
    const updatedConfiguredTerms = configuredTerms.find((term) => term.term === configuredTerm.term)
      ? configuredTerms.map((term) => (term.term === configuredTerm.term ? configuredTerm : term))
      : [...configuredTerms, configuredTerm];
    setConfiguredTerms(updatedConfiguredTerms);
  };

  const resetColorModification = (term: Phenotype) => {
    const updatedConfiguredTerms = configuredTerms.filter((configuredTerm) => term.value !== configuredTerm.term);
    setConfiguredTerms(updatedConfiguredTerms);
  };

  return (
    <Dialog
      {...props}
      ref={props.dialogRef}
      title="Customize Phenotype Color"
      mainButton={{
        label: "Apply",
        onPress: onSubmit,
      }}
    >
      <Card className={style.info_card_for_dialog} scrollable>
        <div>
          {props.phenotype.discreteValues?.length ? (
            <SortableList id="phenotype-value-list">
              <SortableContext items={props.phenotype.discreteValues.map((term: Phenotype) => ({ id: term.value }))}>
                {props.phenotype.discreteValues.map((term: Phenotype, index: number) => {
                  const configuredColor = configuredTerms.find((configuredTerm) => configuredTerm.term === term.value);
                  return (
                    <SortableItem key={`item-${term.value}`} index={index} id={term.value} removeDisabled disabled>
                      <>
                        <ColorButton
                          defaultColor={getCssColor(configuredColor?.color ?? term.color)}
                          onApply={(color) => handleColorModification(color, term)}
                          size="tiny"
                        />
                        <span
                          className={sortableStyle.label}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignContent: "space-between",
                            columnGap: "20px",
                          }}
                        >
                          <div>{term.value}</div>
                          {configuredColor && (
                            <ActionButton
                              label="Reset Color"
                              size={ActionButtonSize.InTableCell}
                              onPress={() => resetColorModification(term)}
                              intent={ActionButtonIntent.Outlined}
                            />
                          )}
                        </span>
                      </>
                    </SortableItem>
                  );
                })}
              </SortableContext>
            </SortableList>
          ) : (
            <ContentPlaceholder text="No term has been defined for this phenotype." />
          )}
        </div>
      </Card>
    </Dialog>
  );
};

export { PhenotypeTermConfigDialogForm };
